import { useQuery, gql } from '@apollo/client';
import React from 'react';

// --- GraphQL Query (Updated) ---
const GET_STUDENT_STATS_FOR_WEEK = gql`
    query GetStudentStatsForWeek($groupId: String!, $weekIndex: Int!) {
        getStudentStatsForWeek(classUid: $groupId, weekIndex: $weekIndex) {
            student {
                id
                username
                surname
                lastname
                class {
                    stats {
                        exits
                        studies
                    }
                }
                boarding
            }
            stats {
                exits
                studies
            }
        }
    }
`;

// --- Helper Components ---

// A new, interactive, and more substantial card design.
function StudentStatCard({ studentStats, href, classStats }) {
    const { student, stats } = studentStats;

    const getInitials = (surname = '', lastname = '') => {
        return `${surname?.[0] || ''}${lastname?.[0] || ''}`.toUpperCase();
    };

    const isExitsExcessive = stats.exits > classStats.exits;
    const isStudiesInsufficient = stats.studies < classStats.studies;

    return (
        <div
            className={`group statsblock block w-full text-left rounded-xl border-2 transition-all duration-100 hover:shadow-md border-slate-200 hover:border-slate-300 ${
                student.boarding
                    ? 'bg-yellow-50'
                    : 'bg-white'
            }`}
        >
            <a href={href} className="block w-full h-full">
                <div className="p-4">
                    <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-xl">
                            {getInitials(student.surname, student.lastname)}
                        </div>
                        <div className="min-w-0 flex-grow">
                            <p className="font-bold text-slate-800 truncate" title={`${student.surname} ${student.lastname}`}>
                               {student.lastname.toUpperCase()} {student.surname} 
                            </p>
                            <p className="text-sm text-slate-500 truncate" title={`@${student.username}`}>
                                @{student.username}
                            </p>
                        </div>
                    </div>
                </div>

                {/* --- Statistics Section (Visually distinct footer) --- */}
                <div className="bg-slate-100 p-4 rounded-b-xl border-t border-slate-200/80">
                    <div className="flex justify-around text-center">
                        <div className="px-2">
                            <span
                                className={`text-3xl font-bold ${
                                    isStudiesInsufficient ? 'text-red-600' : 'text-neutral-600'
                                }`}
                            >
                                {stats.studies}
                            </span>
                            <span className="text-sm font-semibold text-slate-500 tracking-wider"> / </span>
                            <span className="text-sm font-bold text-slate-600">
                                {classStats.studies}
                            </span>
                            <p className="text-xs font-semibold text-slate-500 tracking-wider">
                                Studien
                            </p>
                        </div>
                        <div className="px-2">
                            <span
                                className={`text-3xl font-bold ${
                                    isExitsExcessive ? 'text-red-600' : 'text-neutral-600'
                                }`}
                            >
                                {stats.exits}
                            </span>
                            <span className="text-sm font-semibold text-slate-500 tracking-wider"> / </span>
                            <span className="text-sm font-bold text-slate-600">
                                {classStats.exits}
                            </span>
                            <p className="text-xs font-semibold text-slate-500 tracking-wider">
                                Ausgänge
                            </p>
                        </div>
                    </div>
                </div>
            </a>
        </div>
    );
}

function NoClassSelected() {
    return (
        <div className="w-full text-center py-20 bg-slate-50 rounded-lg">
            <h3 className="mt-2 text-lg font-medium text-slate-800">
                Keine Klasse ausgewählt
            </h3>
            <p className="mt-1 text-sm text-slate-500">Bitte wählen Sie eine Klasse aus, um die Schülerstatistiken anzuzeigen.</p>
        </div>
    );
}

// --- Main Component ---
export default function ClassStatsGrid({ selectedClass, weekIndex }) {
    const { loading, error, data } = useQuery(GET_STUDENT_STATS_FOR_WEEK, {
        variables: { groupId: selectedClass, weekIndex },
        skip: !selectedClass,
    });

    if (!selectedClass) return <NoClassSelected />;
    if (error) return <div className="text-center py-8 text-red-600">Error: {error.message}</div>;

    return (
        <section className="w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                {loading
                    ? Array.from({ length: 10 }).map((_, index) => (
                          <div
                              key={index}
                              className="animate-pulse bg-slate-50 rounded-xl h-32"
                          ></div>
                      ))
                    : data.getStudentStatsForWeek.map((studentStats) => {
                          const student = studentStats.student;
                          const params = [];
                          if (selectedClass) params.push(`class=${encodeURIComponent(selectedClass)}`);
                          if (weekIndex !== undefined && weekIndex !== null) params.push(`week=${encodeURIComponent(String(weekIndex))}`);
                          if (student?.id) params.push(`student=${encodeURIComponent(student.id)}`);
                          const href = `/teacher/students${params.length ? `?${params.join('&')}` : ''}`;

                          return (
                              <StudentStatCard
                                  key={student.id}
                                  studentStats={studentStats}
                                  href={href}
                                  classStats={student.class.stats}
                              />
                          );
                      })}
            </div>
        </section>
    );
}